import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Renderer } from './engine/Renderer';
import { UIShell } from './ui/UIShell';
// Analytics intentionally disabled for initial launch — no Plausible account configured.
// Re-enable by wrapping the tree in <PlausibleProvider> from './analytics/plausible'
// and deploying the /workers/plausible-proxy/ Cloudflare Worker.
import { useSources } from './content/useSources';
import { NavController } from './navigation/NavController';

/**
 * SourcesLoader — tiny side-effect-only component that fires the
 * `useSources` hook at App root so the sources.json fetch starts on page
 * load (not on first click). This makes the <200ms click-to-render target
 * for SourcesPanel achievable: the data is already in the Zustand store by
 * the time any mesh is clicked.
 *
 * Returns null — no render surface, nothing to fail downstream. Mounted
 * OUTSIDE the ErrorBoundary so a scene-render error still lets sources
 * finish loading in the background.
 */
function SourcesLoader() {
  useSources();
  return null;
}

/**
 * Top-level error boundary. Catches:
 *   - Zod parse errors from loadWorldManifest (malformed world.json)
 *   - GLTFLoader decoder 404s (missing Draco/KTX2 WASM)
 *   - R3F render errors (bad geometry/material props)
 *   - Any React render error in the tree below.
 *
 * Surfaces a period-styled fallback instead of a blank white screen so
 * visitors who hit a manifest regression still see "Edgewood Lake" chrome.
 */
class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[App] ErrorBoundary caught:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: '#FDF6EE',
            color: '#3B2F2F',
            fontFamily: '"Goudy Old Style", Georgia, serif',
            padding: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontWeight: 400, letterSpacing: '0.05em' }}>
            Edgewood Lake
          </h1>
          <p>Something went wrong loading the scene.</p>
          <pre
            style={{
              fontSize: '0.75rem',
              opacity: 0.6,
              maxWidth: '60ch',
              whiteSpace: 'pre-wrap',
            }}
          >
            {this.state.error.message}
          </pre>
          <p style={{ marginTop: '2rem', fontSize: '0.875rem' }}>
            Try reloading the page. If it persists, the world manifest may be
            malformed.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * App composition (Plan 05-01 Task 1 restructure):
 *   <PlausibleProvider>         — injects first-party analytics script on prod host only
 *     <SourcesLoader>            — fires useSources (Plan 04-03)
 *     <ErrorBoundary>            — catches Zod/GLTFLoader/R3F render errors
 *       <NavController>          — XState machine + NavSendContext.Provider
 *                                  (Plan 05-01). Wraps BOTH the Canvas tree
 *                                  (StreetcarRig fires END_OF_LINE via
 *                                  useNavSend) AND the DOM overlay tree
 *                                  (BoardingOverlay / ReturnToStreetcarButton
 *                                  dispatch START / REBOARD via useNavSend).
 *         <Renderer>             — R3F Canvas with SceneGraph + <NavRig /> (Canvas-scope
 *                                  rig selector reading navState from useNavStore)
 *         <UIShell>              — LoadingHUD + BoardingOverlay + SourcesPanel +
 *                                  EraToggle + BoundaryFeedback + Crosshair + DebugHUD
 *       </NavController>
 *     </ErrorBoundary>
 *   </PlausibleProvider>
 *
 * PlausibleProvider is outermost so that the analytics script-injection effect
 * runs even if the inner tree throws.
 */
export function App() {
  return (
    <>
      <SourcesLoader />
      <ErrorBoundary>
        <NavController>
          <Renderer />
          <UIShell />
        </NavController>
      </ErrorBoundary>
    </>
  );
}
